/**
 * API client for backend communication
 */
import { setServerWindow } from '@/lib/bookableWindow';
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '@/lib/apiBase';
import { requestHadToken } from '@/lib/authSession';
import { renewAndRetry } from '@/lib/tokenRefresh';
import { currentLocale } from '@/lib/i18n';
import type { NextPayout, PayoutAccount, PayoutBank, PayoutRun, PayoutRunDetail } from '@/lib/payouts';
import type { ChatHistory, ChatMessage } from '@/lib/drillChat';
import type { Notice, NoticeList } from '@/lib/notices';
import type {
  LoginRequest,
  TokenResponse,
  UserInfo,
  PaginatedWorkshops,
  WorkshopDetail,
  SkillScore,
  ConfigVersion,
  ConfigComponent,
  DLQEntry,
  DLQStats,
  CostSummary,
  DailyCost,
  CostByProvider,
  RateLimiter,
  HealthCheck,
  SystemMetrics,
  CacheStats,
  ProviderHealth,
  CacheConfig,
} from '@/types';
import type { MicroClassListItem } from '@/types/micro-class-builder';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('fp-access-token-v1');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// F1 (2026-06-12) — global 401 handler. When the JWT expires every
// endpoint 401s; without this, react-query queued retries each fire
// their own "Failed to load" toast and the UI stays mounted on a
// dead token. This handler clears the token + reloads ONCE no matter
// how many concurrent 401s land in the same tick.
//
// 2026-06-14 (Nino "Ugly" sweep): only bounce when the failing
// request was tied to recent user interaction. Background polls fail
// silently so the user isn't logged out mid-task. Mirror of the CC fix.
const USER_INTERACTION_WINDOW_MS = 10_000;
let _lastInteractionAt = Date.now();
if (typeof window !== 'undefined') {
  const tick = () => { _lastInteractionAt = Date.now(); };
  window.addEventListener('mousedown', tick, { passive: true });
  window.addEventListener('keydown', tick, { passive: true });
  window.addEventListener('focus', tick, { passive: true });
}

/**
 * Studio (/facilitator) on a 401 (Quality, 28 Oct): renew the sign-in once
 * and retry, or sign out with "Your sign-in has expired" (lib/authSession).
 * Never remove the token alone and reload: that left Studio "signed in"
 * with no token, so every page said "check your connection" (and, on a
 * reload, could bounce forever). Elsewhere (the admin app): to /login.
 */
function onUnauthorized(instance: AxiosInstance, error: AxiosError): Promise<unknown> {
  if (!window.location.pathname.startsWith('/facilitator')) {
    localStorage.removeItem('fp-access-token-v1');
    window.location.href = '/login';
  } else if (requestHadToken(error.config?.headers)) {
    return renewAndRetry(instance, error);
  }
  return Promise.reject(error);
}

let _has401Bounced = false;
function handle401Once() {
  if (_has401Bounced) return;
  // Only redirect on recent user interaction; otherwise silent fail.
  const sinceInteraction = Date.now() - _lastInteractionAt;
  if (sinceInteraction > USER_INTERACTION_WINDOW_MS) {
     
    console.warn(
      '[fp-api] 401 on background request (no interaction in last',
      Math.round(sinceInteraction / 1000) + 's). Not redirecting.',
    );
    return;
  }
  _has401Bounced = true;
  try {
    localStorage.removeItem('fp-access-token-v1');
  } catch {
    // private-mode storage failure — fall through to reload anyway
  }
  // Reset the FP to the bare URL so the basename redirect (dev)
  // and the AuthGate (no token → unauthenticated state) take over.
  window.location.assign('/facilitator/');
}

/**
 * 2026-07-19 (Item 7 / FP-authz [GATE], Fable finding — coordinated
 * ahead of Scoring AUTHZ-3/-4 route locks): distinguish 403 from 401
 * so consumer views can render a "Missing permission" state instead
 * of a permanently-spinning "Loading…" when a route was locked to a
 * role this facilitator doesn't have. The 401 path still bounces to
 * login via handle401Once(); 403 rejects with a tagged error the
 * shared `isForbiddenError()` helper picks up in consumer components.
 */
export class FacilitatorForbiddenError extends Error {
  status = 403 as const;
  /** URL the backend rejected. Useful for the surface-level toast. */
  path: string;
  constructor(path: string, message = 'Missing permission') {
    super(message);
    this.name = 'FacilitatorForbiddenError';
    this.path = path;
  }
}

/** Type-guard for consumer surfaces to render a permission-error state. */
export function isForbiddenError(err: unknown): err is FacilitatorForbiddenError {
  if (err instanceof FacilitatorForbiddenError) return true;
  // React Query wraps errors; also handle raw Axios errors for direct
  // consumers that don't use React Query.
  const anyErr = err as { name?: string; status?: number; response?: { status?: number } };
  return anyErr?.name === 'FacilitatorForbiddenError'
    || anyErr?.status === 403
    || anyErr?.response?.status === 403;
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (window.location.pathname.startsWith('/facilitator')) return onUnauthorized(api, error);
      handle401Once();
    } else if (error.response?.status === 403) {
      // Preserve the axios error but attach a tagged wrapper so
      // React Query error boundaries + consumer guards can react.
      const path = String(error.config?.url ?? '');
      return Promise.reject(new FacilitatorForbiddenError(path));
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authApi = {
  login: (credentials: LoginRequest) =>
    api.post<TokenResponse>('/auth/login', credentials),

  logout: () => api.post('/auth/logout'),

  getCurrentUser: () => api.get<UserInfo>('/auth/me'),

  refreshToken: () => api.post<TokenResponse>('/auth/refresh'),
};

// Workshops API
export const workshopsApi = {
  list: async (params: {
    page?: number;
    page_size?: number;
    status_filter?: string;
    search?: string;
  }) => {
    const response = await api.get<PaginatedWorkshops>('/workshops', { params });
    return response.data;
  },

  get: (workshopId: string) =>
    api.get<WorkshopDetail>(`/workshops/${workshopId}`),

  getScores: (workshopId: string) =>
    api.get<SkillScore[]>(`/workshops/${workshopId}/scores`),

  delete: (workshopId: string) => api.delete(`/workshops/${workshopId}`),

  retry: (workshopId: string) =>
    api.post(`/workshops/${workshopId}/retry`),

  upload: (formData: FormData, onProgress?: (progress: any) => void) =>
    api.post('/workshops/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
    }),

  // Results API
  getResults: async (workshopId: string) => {
    const response = await api.get(`/workshops/${workshopId}/results`);
    return response.data;
  },

  getBiasAnalysis: async (workshopId: string) => {
    const response = await api.get(`/workshops/${workshopId}/bias`);
    return response.data;
  },

  getEvidence: async (workshopId: string, skillId?: string) => {
    const url = skillId
      ? `/workshops/${workshopId}/evidence?skill_id=${skillId}`
      : `/workshops/${workshopId}/evidence`;
    const response = await api.get(url);
    return response.data;
  },

  // Export methods
  downloadPDF: async (workshopId: string) => {
    const response = await api.get(`/workshops/${workshopId}/report/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `workshop-${workshopId}-report.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportCSV: async (workshopId: string) => {
    const response = await api.get(`/workshops/${workshopId}/export/csv`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `workshop-${workshopId}-scores.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  exportJSON: async (workshopId: string) => {
    const response = await api.get(`/workshops/${workshopId}/export/json`);
    const dataStr = JSON.stringify(response.data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `workshop-${workshopId}-data.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Backward compatibility alias
export const workshopAPI = workshopsApi;

// Configuration API
export const configApi = {
  listVersions: (params: { component?: string; limit?: number }) =>
    api.get<{ versions: ConfigVersion[]; total: number }>('/config/versions', {
      params,
    }),

  getVersion: (versionId: string) =>
    api.get<ConfigVersion>(`/config/versions/${versionId}`),

  getActive: (component?: string) =>
    api.get<{ active_configs: ConfigVersion[]; total: number }>(
      '/config/active',
      { params: { component } }
    ),

  activateVersion: (versionId: string) =>
    api.post(`/config/versions/${versionId}/activate`),

  compareVersions: (versionId: string, compareVersionId: string) =>
    api.get(`/config/versions/${versionId}/diff/${compareVersionId}`),

  listComponents: () =>
    api.get<{ components: ConfigComponent[]; total: number }>(
      '/config/components'
    ),
};

// DLQ API
export const dlqApi = {
  list: (params: { page?: number; page_size?: number; resolved?: boolean }) =>
    api.get<{
      items: DLQEntry[];
      total: number;
      page: number;
      page_size: number;
      pages: number;
    }>('/dlq', { params }),

  get: (entryId: string) => api.get<DLQEntry>(`/dlq/${entryId}`),

  resolve: (entryId: string, notes?: string) =>
    api.post(`/dlq/${entryId}/resolve`, { notes }),

  retry: (entryId: string) => api.post(`/dlq/${entryId}/retry`),

  getStats: () => api.get<DLQStats>('/dlq/stats'),
};

// Costs API
export const costsApi = {
  getSummary: (days?: number) =>
    api.get<CostSummary>('/costs/summary', { params: { days } }),

  getDaily: (days?: number) =>
    api.get<{ period_days: number; daily_costs: DailyCost[] }>(
      '/costs/daily',
      { params: { days } }
    ),

  getByProvider: (days?: number) =>
    api.get<{ period_days: number; by_provider: CostByProvider[] }>(
      '/costs/by-provider',
      { params: { days } }
    ),

  getByModel: (days?: number) =>
    api.get<{ period_days: number; by_model: CostByProvider[] }>(
      '/costs/by-model',
      { params: { days } }
    ),
};

// Rate Limiters API
export const rateLimitersApi = {
  list: () =>
    api.get<{ limiters: RateLimiter[]; total: number }>('/rate-limiters'),

  get: (limiterId: string) =>
    api.get<RateLimiter>(`/rate-limiters/${limiterId}`),

  reset: (limiterId: string) =>
    api.post(`/rate-limiters/${limiterId}/reset`),
};

// System API
export const systemApi = {
  health: () => api.get<HealthCheck>('/system/health'),

  info: () => api.get('/system/info'),

  metrics: () => api.get<SystemMetrics>('/system/metrics'),

  tables: () => api.get('/system/database/tables'),
};

// Cache API
export const cacheApi = {
  getStats: () => api.get<CacheStats>('/cache/stats'),

  getProviderHealth: () => api.get<ProviderHealth>('/cache/providers/health'),

  clearCache: (service?: 'transcription' | 'diarization') =>
    api.post('/cache/clear', null, {
      params: service ? { service } : {},
    }),

  invalidateEntry: (audioFilePath: string, service: 'transcription' | 'diarization') =>
    api.post('/cache/invalidate', null, {
      params: { audio_file_path: audioFilePath, service },
    }),

  getHealth: () => api.get('/cache/health'),

  getConfig: () => api.get<CacheConfig>('/cache/config'),
};

// ── Scheduler API ─────────────────────────────────────────────────────
// Uses /api/scheduler prefix (separate from /api/v1)
const schedulerApi: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/scheduler`,
  headers: { 'Content-Type': 'application/json' },
});

schedulerApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('fp-access-token-v1');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // The portal's language, so server messages match it (i18n, 1 Oct).
    if (config.headers) config.headers['X-Playbook-Locale'] = currentLocale();
    return config;
  },
  (error) => Promise.reject(error)
);

schedulerApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Only redirect to /login when in the admin app context.
      // The facilitator portal runs at /facilitator/* and handles
      // its own auth flow — redirecting there would break it.
      if (!window.location.pathname.startsWith('/facilitator')) {
        localStorage.removeItem('fp-access-token-v1');
        window.location.href = '/login';
      } else if (requestHadToken(error.config?.headers)) {
        // FP: renew the sign-in once and retry; sign out only if that fails.
        return renewAndRetry(schedulerApi, error);
      }
    }
    return Promise.reject(error);
  }
);

export const schedulerApiClient = {
  // Facilitators
  listFacilitators: async (status?: string) => {
    const response = await schedulerApi.get('/facilitators', { params: status ? { status } : {} });
    return response.data;
  },
  createFacilitator: async (data: {
    display_name: string;
    email: string;
    timezone?: string;
    qualified_workshops?: string[];
    max_sessions_per_day?: number;
    max_sessions_per_week?: number;
  }) => {
    const response = await schedulerApi.post('/facilitators', data);
    return response.data;
  },
  updateFacilitator: async (facilitatorId: string, data: Record<string, any>) => {
    const response = await schedulerApi.patch(`/facilitators/${facilitatorId}`, data);
    return response.data;
  },

  // Sessions
  listSessions: async (params: {
    status?: string;
    facilitator_id?: string;
    from_date?: string;
    to_date?: string;
  }) => {
    const response = await schedulerApi.get('/sessions', { params });
    return response.data;
  },
  createSession: async (data: {
    workshop_id: string;
    workshop_title: string;
    short_title?: string;
    facilitator_id?: string;
    scheduled_start: string;
    duration_minutes: number;
    max_participants?: number;
    notes?: string;
  }) => {
    const response = await schedulerApi.post('/sessions', data);
    return response.data;
  },
  updateSession: async (sessionId: string, data: Record<string, any>) => {
    const response = await schedulerApi.patch(`/sessions/${sessionId}`, data);
    return response.data;
  },
  // cancelSession removed 2 Oct: that route is admin-only, and a facilitator
  // gives up a session through Withdraw (facilitatorWithdraw).
  startSession: async (sessionId: string, data?: { mode?: string; role_tier?: string }) => {
    const response = await schedulerApi.post(`/sessions/${sessionId}/start`, data || {});
    return response.data;
  },

  // Bookings
  getSessionBookings: async (sessionId: string) => {
    const response = await schedulerApi.get(`/sessions/${sessionId}/bookings`);
    return response.data;
  },
  bookSession: async (sessionId: string, data: { learner_id: string; role_assignment?: string }) => {
    const response = await schedulerApi.post(`/sessions/${sessionId}/book`, data);
    return response.data;
  },
  cancelBooking: async (bookingId: string) => {
    const response = await schedulerApi.delete(`/bookings/${bookingId}`);
    return response.data;
  },

  // Individual facilitator
  getFacilitator: async (facilitatorId: string) => {
    const response = await schedulerApi.get(`/facilitators/${facilitatorId}`);
    return response.data;
  },
  approveFacilitator: async (facilitatorId: string) => {
    const response = await schedulerApi.post(`/facilitators/${facilitatorId}/approve`);
    return response.data;
  },
  rejectFacilitator: async (facilitatorId: string, reason: string) => {
    const response = await schedulerApi.post(`/facilitators/${facilitatorId}/reject`, { reason });
    return response.data;
  },
  deactivateFacilitator: async (facilitatorId: string) => {
    const response = await schedulerApi.post(`/facilitators/${facilitatorId}/deactivate`);
    return response.data;
  },
  getAvailability: async (facilitatorId: string) => {
    // Backend returns {facilitator_id, slots: [...]}. Unwrap so callers
    // get a plain array, matching the shape AvailabilityTab.loadAvailability
    // and any future scheduler UI naturally expects.
    const response = await schedulerApi.get(`/facilitators/${facilitatorId}/availability`);
    return response.data?.slots ?? [];
  },

  // Catalogue
  getCatalogue: async (params?: { from_date?: string; to_date?: string; workshop_title?: string }) => {
    const response = await schedulerApi.get('/catalogue', { params });
    return response.data;
  },

  // ── Facilitator Portal endpoints ───────────────────────────────────
  loginFacilitator: async (data: { email: string; password: string }) => {
    const response = await schedulerApi.post('/facilitators/login', data);
    return response.data;
  },
  // Email-code sign-in (Scoring 2026-09-29). Same engine and email as
  // learners; a facilitator code only opens the facilitator door, no credits.
  // 202 {status, expires_in_s, resend_after_s} · 429 {reason, retry_after_s}
  // · 502 {reason: send_failed} (nothing was sent).
  requestFacilitatorCode: async (email: string, language: 'en' | 'id' = 'en') => {
    const response = await schedulerApi.post('/facilitators/code/request', { email, language });
    return response.data as { status: string; expires_in_s: number; resend_after_s: number };
  },
  // 200: /facilitators/login's shape + is_new_account. A new email creates
  // the facilitator with display_name "". 400 {reason: code_invalid
  // (+attempts_remaining) | code_expired | code_locked}.
  verifyFacilitatorCode: async (email: string, code: string, timezone?: string) => {
    const response = await schedulerApi.post('/facilitators/code/verify', { email, code, timezone });
    return response.data;
  },
  registerFacilitator: async (data: {
    display_name: string;
    email: string;
    password: string;
    timezone: string;
    qualified_workshops?: string[];
    max_sessions_per_day?: number;
    max_sessions_per_week?: number;
    profile_data?: Record<string, unknown>;
  }) => {
    const response = await schedulerApi.post('/facilitators/register', data);
    return response.data;
  },
  setAvailability: async (facilitatorId: string, slots: Array<Record<string, unknown>>) => {
    const response = await schedulerApi.put(`/facilitators/${facilitatorId}/availability`, { slots });
    return response.data;
  },
  getProposals: async () => {
    const response = await schedulerApi.get('/proposals');
    return response.data;
  },
  respondToProposal: async (proposalId: string, action: string, reason?: string) => {
    const response = await schedulerApi.post(`/proposals/${proposalId}/respond`, {
      action,
      reason: reason || null,
    });
    return response.data;
  },
  getSessionWorkshopContent: async (sessionId: string) => {
    const response = await schedulerApi.get(`/sessions/${sessionId}/workshop-content`);
    return response.data;
  },
  /** Returns the raw, unflattened published workshop JSON by
   *  workshop_id. Used by the FP WorkshopConsole to render the full
   *  v2 shape (hiddenContext, redLines, successCriteria, scene
   *  evidenceContract). Skip the session detour — bridge sessions
   *  carry workshop_id directly. */
  getWorkshopRawContent: async (workshopId: string) => {
    const response = await schedulerApi.get(`/workshops/${workshopId}/raw-content`);
    return response.data;
  },
  facilitatorWithdraw: async (sessionId: string) => {
    const response = await schedulerApi.post(`/sessions/${sessionId}/facilitator-withdraw`);
    return response.data;
  },
};

// ── Local Results API ──────────────────────────────────────────────
// Surfaces the JSONs written by `scripts/score_local_audio.py` so the
// FP "Results" tab can render them in a friendly way without going
// through the production S3 → Celery path.

const localResultsAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/local-results`,
  headers: { 'Content-Type': 'application/json' },
});
localResultsAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwt_token');
  if (token && config.headers) (config.headers as any).Authorization = `Bearer ${token}`;  
  return config;
});

export interface LocalResultSummary {
  filename: string;
  workshop_id: string;
  // Friendly title from data/content_store/workshops/. Falls back to
  // workshop_id when no content match exists. Always present (server
  // always sends a string, never null).
  workshop_title: string;
  generated_at?: string;
  modified_at?: number;
  processing_time?: number;
  skills_scored?: number;
  utterance_count?: number;
  evidence_count?: number;
  pipeline_version?: string;
  algorithm_version?: string;
}

// CC Test Workshops aggregate shape — server pre-computes per-pillar
// averages so the React tab can render rings without owning a 135-skill
// → pillar lookup table.
export interface AggregatedSpeaker {
  speaker_id: string;
  // Operator-supplied real name (from the renames sidecar). Null until
  // the speaker has been identified + renamed in the CC.
  display_name: string | null;
  // Per-pillar mean score. `null` means no firing evidence in that
  // pillar — render as "no data", not as 0.0. Avoids the unfair
  // "Viki got 3.0 average even though she barely spoke" problem.
  pillar_scores: {
    character: number | null;
    collaboration: number | null;
    communication: number | null;
    creativity: number | null;
    critical_thinking: number | null;
  };
  // Skills assessed per pillar (count of L4 skills with at least one
  // firing verdict).
  pillar_skill_counts: {
    character: number;
    collaboration: number;
    communication: number;
    creativity: number;
    critical_thinking: number;
  };
  // Total firing verdicts per pillar — best signal of "how much did
  // this person contribute on this pillar". Surfaced next to each bar.
  pillar_evidence_counts: {
    character: number;
    collaboration: number;
    communication: number;
    creativity: number;
    critical_thinking: number;
  };
  scored_skill_count: number;
  top_skills: Array<{
    skill_id: string;
    skill_name: string;
    score: number;
    level: string;
    pillar: string | null;
  }>;
  utterance_count: number;
  // Three representative utterances (earliest, middle, longest) so
  // the operator can identify each anonymous SPEAKER_NN by voice/words.
  sample_utterances: Array<{
    start_time: number;
    text: string;
  }>;
}

export interface AggregatedResult {
  filename: string;
  workshop_id: string;
  workshop_title: string;
  generated_at?: string;
  processing_time_seconds: number;
  utterance_count: number;
  speakers: AggregatedSpeaker[];
}

// Pulse — 5-pillar score trajectory over workshop time, one
// trajectory per speaker plus an aggregate (`speaker_id ===
// "WORKSHOP_OVERALL"`). Each bin holds either Bayesian posterior or
// raw empirical mean per pillar (mode chosen by the caller).
export interface PulseBin {
  bin_index: number;
  end_seconds: number;
  skill_count: number;
  pillar_scores: {
    character: number | null;
    collaboration: number | null;
    communication: number | null;
    creativity: number | null;
    critical_thinking: number | null;
  };
}

/** Per-pillar first→last delta over a trajectory. `null` for pillars
 *  with no evidence at all. `direction` collapses small deltas to
 *  'flat' so the UI can show meaningful arrows. */
export interface PulsePillarTrend {
  start: number;
  end: number;
  delta: number;
  direction: 'up' | 'down' | 'flat';
}

export interface PulseTrajectory {
  speaker_id: string;
  display_name: string;
  evidence_count: number;
  bins: PulseBin[];
  pillar_trend?: {
    character: PulsePillarTrend | null;
    collaboration: PulsePillarTrend | null;
    communication: PulsePillarTrend | null;
    creativity: PulsePillarTrend | null;
    critical_thinking: PulsePillarTrend | null;
  };
}

/** Workshop-overall verdict counts per time bin. Drives the density
 *  strip above the chart when "All speakers" is selected — quickly
 *  shows where in the workshop activity peaked. */
export interface PulseDensityBin {
  bin_index: number;
  end_seconds: number;
  verdict_count: number;
}

export interface PulseSeries {
  filename: string;
  mode: 'bayesian' | 'raw';
  bin_size_seconds: number;
  duration_seconds: number;
  trajectories: PulseTrajectory[];
  density_bins?: PulseDensityBin[];
  matched_evidence_count: number;
  dropped_evidence_count: number;
  note?: string;
}

/** Reserved sentinel for utterances tagged as silence / nobody. The
 * backend drops these from scoring entirely. Surfaced in dropdowns
 * as "— Silence / nobody —". */
export const NOBODY_SPEAKER_ID = '__NOBODY__';

/** One split point inside an utterance — `after_word` is the index
 * of the word AFTER which the split occurs (0-indexed; 0 means "the
 * very first segment uses this speaker_id"). */
export interface UtteranceSplit {
  after_word: number;
  speaker_id: string;
}

/** Per-utterance operator edits, all keyed by utterance index. */
export interface UtteranceOverrides {
  /** Whole-utterance speaker reassignment. Use NOBODY_SPEAKER_ID to
   *  drop the utterance from scoring (silence / hallucination). */
  speakers: Record<string, string>;
  /** Mid-utterance speaker changes. Each value is a list of split
   *  points in ascending after_word order. Splits override `speakers`
   *  for that utterance. */
  splits: Record<string, UtteranceSplit[]>;
  /** Cosmetic text correction. Display-only — does NOT trigger
   *  LLM re-judging; existing verdicts stay matched against the
   *  original Whisper text. */
  text: Record<string, string>;
}

/** Convert any shape the backend might return into the canonical
 *  structured shape. Handles three cases:
 *    1. New structured shape — passed through after defensive trims.
 *    2. Legacy flat shape `{idx: speaker}` — migrated into
 *       `{speakers: {...}, splits: {}, text: {}}`. Surfaces while the
 *       backend is still on the pre-bundle build.
 *    3. null / undefined / unknown — empty structured shape. */
export function normaliseUtteranceOverrides(raw: unknown): UtteranceOverrides {
  const empty: UtteranceOverrides = { speakers: {}, splits: {}, text: {} };
  if (!raw || typeof raw !== 'object') return empty;
  const obj = raw as Record<string, unknown>;

  // Detect legacy flat shape: every key is a digit-string and every
  // value is a string. (The structured shape has at most three keys —
  // `speakers`, `splits`, `text` — none of which are digit-strings.)
  const entries = Object.entries(obj);
  const looksFlat =
    entries.length > 0 &&
    entries.every(
      ([k, v]) => /^\d+$/.test(k) && typeof v === 'string',
    );
  if (looksFlat) {
    const speakers: Record<string, string> = {};
    for (const [k, v] of entries) {
      if (typeof v === 'string' && v.trim()) speakers[k] = v.trim();
    }
    return { speakers, splits: {}, text: {} };
  }

  // Structured shape — defensively pluck each section.
  const speakersRaw = obj.speakers;
  const splitsRaw = obj.splits;
  const textRaw = obj.text;

  const speakers: Record<string, string> = {};
  if (speakersRaw && typeof speakersRaw === 'object') {
    for (const [k, v] of Object.entries(speakersRaw as Record<string, unknown>)) {
      if (typeof v === 'string' && v.trim()) speakers[k] = v.trim();
    }
  }

  const splits: Record<string, UtteranceSplit[]> = {};
  if (splitsRaw && typeof splitsRaw === 'object') {
    for (const [k, v] of Object.entries(splitsRaw as Record<string, unknown>)) {
      if (!Array.isArray(v)) continue;
      const segs: UtteranceSplit[] = [];
      for (const item of v) {
        if (!item || typeof item !== 'object') continue;
        const seg = item as { after_word?: unknown; speaker_id?: unknown };
        if (typeof seg.after_word !== 'number') continue;
        if (typeof seg.speaker_id !== 'string' || !seg.speaker_id.trim()) continue;
        segs.push({
          after_word: seg.after_word,
          speaker_id: seg.speaker_id.trim(),
        });
      }
      if (segs.length > 0) {
        segs.sort((a, b) => a.after_word - b.after_word);
        splits[k] = segs;
      }
    }
  }

  const text: Record<string, string> = {};
  if (textRaw && typeof textRaw === 'object') {
    for (const [k, v] of Object.entries(textRaw as Record<string, unknown>)) {
      if (typeof v === 'string' && v.trim()) text[k] = v;
    }
  }

  return { speakers, splits, text };
}

export const localResultsApi = {
  list: async (): Promise<{ count: number; results: LocalResultSummary[] }> => {
    const response = await localResultsAxios.get('');
    return response.data;
  },
  get: async (filename: string): Promise<any> => {   
    const response = await localResultsAxios.get(`/${encodeURIComponent(filename)}`);
    return response.data;
  },
  // CC Test Workshops — per-pillar Bayesian-replay trajectory across
  // workshop time. Server bins by `bin_size_seconds` (default 180 = 3
  // minutes), replays the canonical Dirichlet aggregation with evidence
  // up to each bin endpoint, and aggregates skill posteriors to pillars.
  pulse: async (
    filename: string,
    binSizeSeconds: number = 180,
    mode: 'bayesian' | 'raw' = 'bayesian',
  ): Promise<PulseSeries> => {
    const response = await localResultsAxios.get(
      `/${encodeURIComponent(filename)}/pulse`,
      { params: { bin_size_seconds: binSizeSeconds, mode } },
    );
    return response.data;
  },
  // CC Test Workshops — pre-aggregated per-pillar shape per speaker.
  // Mode picks the score function:
  //   - 'bayesian' (default): Dirichlet posterior with prior
  //     [0.1, 1, 1, 1, 1, 1]. Matches production scoring.
  //   - 'raw': empirical mean of level votes. No prior, no shrinkage.
  //     Wider dynamic range — useful when evidence is thin and the
  //     Bayesian pull-to-3.0 is hiding strong individual firings.
  aggregated: async (
    filename: string,
    mode: 'bayesian' | 'raw' = 'bayesian',
  ): Promise<AggregatedResult> => {
    const response = await localResultsAxios.get(
      `/${encodeURIComponent(filename)}/aggregated`,
      { params: { mode } },
    );
    return response.data;
  },
  // Per-utterance overrides — structured shape covering three classes
  // of operator edits, all keyed by utterance index in the result
  // JSON's `utterances` list:
  //   - speakers: reassign a whole utterance to a different speaker.
  //     Special value '__NOBODY__' tags silence / hallucinations and
  //     drops them from scoring entirely.
  //   - splits:   chop one utterance into N segments at given word
  //               boundaries; each segment gets its own speaker_id.
  //   - text:     cosmetic text correction (does NOT trigger LLM
  //               re-judging; verdicts stay matched against original
  //               Whisper output).
  // Sidecar `{filename}.utterance_overrides.json`. Empty sub-dicts =
  // no overrides applied.
  getUtteranceOverrides: async (
    filename: string,
  ): Promise<{ filename: string; overrides: UtteranceOverrides }> => {
    const response = await localResultsAxios.get(
      `/${encodeURIComponent(filename)}/utterance-overrides`,
    );
    // Normalise — old backend (pre rebuild) returns the legacy flat
    // shape `{overrides: {"12": "SPEAKER_03"}}` directly. Detect that
    // and migrate to the structured shape so the UI never crashes
    // dereferencing `overrides.speakers`.
    const raw = response.data?.overrides;
    return {
      filename: response.data?.filename ?? filename,
      overrides: normaliseUtteranceOverrides(raw),
    };
  },
  saveUtteranceOverrides: async (
    filename: string,
    overrides: UtteranceOverrides,
  ): Promise<{ filename: string; overrides: UtteranceOverrides; saved: boolean }> => {
    const response = await localResultsAxios.put(
      `/${encodeURIComponent(filename)}/utterance-overrides`,
      { overrides },
    );
    return response.data;
  },
  // Hidden speakers — operator-flagged speaker_ids that should NOT
  // surface as participant cards (facilitator, manual exclusions).
  // Sidecar `{filename}.hidden_speakers.json`. IDs match against the
  // collapsed (post-rename) speaker_id so hiding "Maxime" works
  // whether Maxime is a rename target or a free-form override.
  getHiddenSpeakers: async (
    filename: string,
  ): Promise<{ filename: string; hidden: string[] }> => {
    const response = await localResultsAxios.get(
      `/${encodeURIComponent(filename)}/hidden-speakers`,
    );
    return response.data;
  },
  saveHiddenSpeakers: async (
    filename: string,
    hidden: string[],
  ): Promise<{ filename: string; hidden: string[]; saved: boolean }> => {
    const response = await localResultsAxios.put(
      `/${encodeURIComponent(filename)}/hidden-speakers`,
      { hidden },
    );
    return response.data;
  },
  // Speaker renames — operator-supplied real names per result file.
  // Backend stores them in a sidecar JSON next to the result file so
  // the original scoring output stays untouched.
  getRenames: async (filename: string): Promise<{ filename: string; renames: Record<string, string> }> => {
    const response = await localResultsAxios.get(
      `/${encodeURIComponent(filename)}/renames`,
    );
    return response.data;
  },
  saveRenames: async (
    filename: string,
    renames: Record<string, string>,
  ): Promise<{ filename: string; renames: Record<string, string>; saved: boolean }> => {
    const response = await localResultsAxios.put(
      `/${encodeURIComponent(filename)}/renames`,
      { renames },
    );
    return response.data;
  },
  delete: async (filename: string): Promise<{ deleted: string }> => {
    const response = await localResultsAxios.delete(`/${encodeURIComponent(filename)}`);
    return response.data;
  },
};

// ── Bridge API ───────────────────────────────────────────────────────
// Uses /api/bridge prefix (separate from /api/v1)
const bridgeApi: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/bridge`,
  headers: { 'Content-Type': 'application/json' },
});

bridgeApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('fp-access-token-v1');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // The portal's language, so server messages match it (i18n, 1 Oct).
    if (config.headers) config.headers['X-Playbook-Locale'] = currentLocale();
    return config;
  },
  (error) => Promise.reject(error)
);

bridgeApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (window.location.pathname.startsWith('/facilitator')) {
        // Was sending facilitators to /login, the ADMIN sign-in (audit 29 Sep).
        // Renew the sign-in once and retry; sign out only if that fails.
        if (requestHadToken(error.config?.headers)) return renewAndRetry(bridgeApi, error);
      } else {
        localStorage.removeItem('fp-access-token-v1');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const bridgeApiClient = {
  // Sessions
  getActiveSessions: async () => {
    const response = await bridgeApi.get('/sessions/active');
    return response.data;
  },
  getSession: async (sessionId: string) => {
    const response = await bridgeApi.get(`/session/${sessionId}`);
    return response.data;
  },
  // Drill chat (Scoring's format v1, 6 Oct). History on open and on every
  // reconnect (`after` = the last message seen); send by POST.
  chatHistory: async (sessionId: string, after?: string | null): Promise<ChatHistory> => {
    const response = await bridgeApi.get(`/session/${encodeURIComponent(sessionId)}/chat`, { params: after ? { after } : undefined });
    return response.data;
  },
  /** Scoring's /health `features.drill_chat` (6 Oct): true/false once it ships, null before (then the history answer decides). */
  /** Scoring's /health `features` (drill_chat, drill_observer…); null when the server sends none. */
  healthFeatures: async (): Promise<Record<string, unknown> | null> => {
    const response = await axios.get(`${API_BASE_URL}/health`, { timeout: 5_000 });
    const f = response.data?.features;
    return f && typeof f === 'object' ? (f as Record<string, unknown>) : null;
  },
  chatFeature: async (): Promise<boolean | null> => {
    const response = await axios.get(`${API_BASE_URL}/health`, { timeout: 5_000 });
    const flag = response.data?.features?.drill_chat;
    return typeof flag === 'boolean' ? flag : null;
  },
  chatSend: async (sessionId: string, body: { text: string; to: string; client_id: string }): Promise<ChatMessage> => {
    const response = await bridgeApi.post(`/session/${encodeURIComponent(sessionId)}/chat`, body, { timeout: 10_000 });
    return response.data;
  },
  getSessionByCode: async (joinCode: string) => {
    const response = await bridgeApi.get(`/session/by-code/${joinCode}`);
    return response.data;
  },
  createSession: async (data: {
    workshop_id: string;
    workshop_title?: string;
    facilitator_id?: string;
    mode?: string;
    duration_minutes?: number;
    role_tier?: string;
    scenario_text?: string;
    context_text?: string;
    available_twists?: Array<Record<string, any>>;
    /** Optional — when launching from a published ScheduledSession,
     *  pass its session_id so the backend pre-seeds participants
     *  from confirmed bookings. Without this, web join-by-code
     *  rejects every learner with "roster doesn't include you". */
    scheduled_session_id?: string;
  }) => {
    const response = await bridgeApi.post('/session', data);
    return response.data;
  },
  // In person, when the room can't start (Design 17 Oct: "Not allowed"). Routes proposed
  // to Live Drills; the buttons only show once the server sends lobby_state.room.
  switchToOnline: async (sessionId: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/switch-online`);
    return response.data;
  },
  cancelDrill: async (sessionId: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/cancel`);
    return response.data;
  },
  startSession: async (sessionId: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/start`);
    return response.data;
  },
  endSession: async (sessionId: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/end`);
    return response.data;
  },
  changePhase: async (sessionId: string, phase: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/phase`, { phase });
    return response.data;
  },

  // Participants
  //
  // 2026-09-28 (Product + Scoring): email is the load-bearing field
  // for ad-hoc adds. Server resolves email → learner_id, and the
  // roster match on join-by-code depends on that resolution. Pass
  // both `email` and `name` in the payload during the transition —
  // the server prefers email; older code paths that expected `name`
  // still see it. When Scoring confirms email is the only accepted
  // key, drop `name` from the payload here in a follow-up.
  addParticipant: async (sessionId: string, data: {
    /** Learner email — REQUIRED for ad-hoc adds so server can resolve
     *  to learner_id. Bare-name adds silently fail on join-by-code. */
    email: string;
    /** Kept for transition compatibility; usually mirrors `email`. */
    name?: string;
    /** Optional (Scoring, 3 Oct): missing or taken → the server gives the next free role. */
    role?: string;
    learner_id?: string;
    role_title?: string;
    role_description?: string;
    role_hidden_info?: string;
  }) => {
    const response = await bridgeApi.post(`/session/${sessionId}/participants`, data);
    return response.data;
  },
  removeParticipant: async (sessionId: string, participantId: string) => {
    const response = await bridgeApi.delete(`/session/${sessionId}/participants/${participantId}`);
    return response.data;
  },

  // Role Cards
  releaseRoles: async (sessionId: string) => {
    const response = await bridgeApi.post(`/session/${sessionId}/release-roles`);
    return response.data;
  },
  getRoleStatus: async (sessionId: string) => {
    const response = await bridgeApi.get(`/session/${sessionId}/role-status`);
    return response.data;
  },

  // Twists
  fireTwist: async (sessionId: string, data: {
    twist_id: string;
    title: string;
    intensity: string;
    instructions?: string;
  }) => {
    const response = await bridgeApi.post(`/session/${sessionId}/fire-twist`, data);
    return response.data;
  },
  getTwistStatus: async (sessionId: string) => {
    const response = await bridgeApi.get(`/session/${sessionId}/twist-status`);
    return response.data;
  },

  // Scoring Metadata
  getScoringMetadata: async (sessionId: string) => {
    const response = await bridgeApi.get(`/session/${sessionId}/scoring-metadata`);
    return response.data;
  },

  // 2026-09-25: the REST stub `getFacilitatorRoomToken` that lived
  // here on Day 1 has been deleted. Scoring's memo settled the shape:
  // the facilitator's LiveKit credentials ride the existing
  // `session_info` message on the Bridge WS, under `livekit_url`,
  // `livekit_room_name`, `livekit_join_token`. `useBridgeSession`
  // captures them and `useFacilitatorRoom` consumes them. No HTTP
  // round-trip involved.
};

// ── Micro-Class API ──────────────────────────────────────────────────
// Uses /api/micro-class prefix
const microClassAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/micro-class`,
  headers: { 'Content-Type': 'application/json' },
});

microClassAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('fp-access-token-v1');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

microClassAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => (error.response?.status === 401 ? onUnauthorized(microClassAxios, error) : Promise.reject(error)),
);

export const microClassApi = {
  // Classes — uses /catalogue endpoint (backend's actual route)
  list: async (params?: { status?: string; skill_id?: string; limit?: number; offset?: number }) => {
    const response = await microClassAxios.get('/catalogue', { params });
    // Backend returns { items: [...] } — unwrap and normalize IDs
    const data = response.data;
    const items = data.microClasses || data.items || data;
    // Catalogue items use micro_class_id; normalize to 'id' + mark as published
    return (Array.isArray(items) ? items : []).map((item: Record<string, unknown>) => ({
      ...item,
      id: item.id || item.micro_class_id || item.microClassId,
      status: 'published',
      skills_assessed: item.skills_assessed || item.skills,
    })) as MicroClassListItem[];
  },
  listDrafts: async (params?: { status?: string }) => {
    const response = await microClassAxios.get('/classes', { params });
    const data = response.data;
    return data.drafts || data.items || data;
  },
  get: async (classId: string) => {
    // Try draft endpoint first (has full form data), fall back to catalogue
    try {
      const response = await microClassAxios.get(`/classes/${classId}`);
      return response.data;
    } catch {
      const response = await microClassAxios.get(`/catalogue/${classId}`);
      return response.data;
    }
  },
  getPublished: async (classId: string) => {
    const response = await microClassAxios.get(`/catalogue/${classId}`);
    return response.data;
  },
  create: async (data: Record<string, unknown>) => {
    const response = await microClassAxios.post('/classes', data);
    return response.data;
  },
  update: async (classId: string, data: Record<string, unknown>) => {
    const response = await microClassAxios.patch(`/classes/${classId}`, data);
    return response.data;
  },
  delete: async (classId: string) => {
    const response = await microClassAxios.delete(`/classes/${classId}`);
    return response.data;
  },
  publish: async (classId: string) => {
    const response = await microClassAxios.post(`/classes/${classId}/publish`);
    return response.data;
  },
  retire: async (classId: string) => {
    const response = await microClassAxios.post(`/classes/${classId}/retire`);
    return response.data;
  },

  // Rubrics
  getRubric: async (skillId: string) => {
    const response = await microClassAxios.get(`/rubrics/${skillId}`);
    return response.data;
  },

  // Generation pipeline
  generateScript: async (classId: string, model?: string) => {
    const response = await microClassAxios.post(`/classes/${classId}/generate-script`, model ? { model } : {});
    return response.data;
  },
  auditSkills: async (classId: string, model?: string) => {
    const response = await microClassAxios.post(`/classes/${classId}/audit-skills`, model ? { model } : {});
    return response.data;
  },
  generateAudio: async (classId: string) => {
    const response = await microClassAxios.post(`/classes/${classId}/generate-audio`);
    return response.data;
  },
  getPipelineStatus: async (classId: string) => {
    const response = await microClassAxios.get(`/classes/${classId}/pipeline-status`);
    return response.data;
  },
};

// ── LLM Proxy API ────────────────────────────────────────────────────
// Uses /api/llm prefix
const llmAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/llm`,
  headers: { 'Content-Type': 'application/json' },
});

llmAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('fp-access-token-v1');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

llmAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => (error.response?.status === 401 ? onUnauthorized(llmAxios, error) : Promise.reject(error)),
);

export const llmProxyApi = {
  getProviders: async () => {
    const response = await llmAxios.get('/providers');
    return response.data;
  },
  getPrompts: async () => {
    const response = await llmAxios.get('/prompts');
    return response.data;
  },
  getPrompt: async (promptId: string) => {
    const response = await llmAxios.get(`/prompts/${promptId}`);
    return response.data;
  },
  updatePrompt: async (promptId: string, data: Record<string, unknown>) => {
    const response = await llmAxios.patch(`/prompts/${promptId}`, data);
    return response.data;
  },
  testPrompt: async (data: { prompt_id: string; variables?: Record<string, string> }) => {
    const response = await llmAxios.post('/prompts/test', data);
    return response.data;
  },
};

// ── TTS API ──────────────────────────────────────────────────────────
// Uses /api/tts prefix
const ttsAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/tts`,
  headers: { 'Content-Type': 'application/json' },
});

ttsAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('fp-access-token-v1');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

ttsAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => (error.response?.status === 401 ? onUnauthorized(ttsAxios, error) : Promise.reject(error)),
);

export const ttsApi = {
  getVoices: async () => {
    const response = await ttsAxios.get('/voices');
    return response.data;
  },
  getConfig: async () => {
    const response = await ttsAxios.get('/config');
    return response.data;
  },
  updateConfig: async (data: Record<string, unknown>) => {
    const response = await ttsAxios.patch('/config', data);
    return response.data;
  },
  preview: async (data: { text: string; voice_id?: string }) => {
    const response = await ttsAxios.post('/preview', data, { responseType: 'blob' });
    return response.data;
  },
};

// ── Facilitator Analytics API ────────────────────────────────────────
export const facilitatorAnalyticsApi = {
  getFeedback: async (facilitatorId: string) => {
    const response = await api.get(`/analytics/facilitators/${facilitatorId}/feedback`);
    return response.data;
  },
  getScore: async (facilitatorId: string) => {
    const response = await api.get(`/analytics/facilitators/${facilitatorId}/score`);
    return response.data;
  },
  recomputeScore: async (facilitatorId: string) => {
    const response = await api.post(`/analytics/facilitators/${facilitatorId}/score/recompute`);
    return response.data;
  },
  recomputeAll: async () => {
    const response = await api.post('/analytics/scores/recompute-all');
    return response.data;
  },
};

// ── Content Pipeline API ─────────────────────────────────────────────
export const contentPipelineApi = {
  list: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const response = await api.get('/content-pipeline/jobs', { params });
    return response.data;
  },
  get: async (jobId: string) => {
    const response = await api.get(`/content-pipeline/jobs/${jobId}`);
    return response.data;
  },
  retry: async (jobId: string) => {
    const response = await api.post(`/content-pipeline/jobs/${jobId}/retry`);
    return response.data;
  },
  cancel: async (jobId: string) => {
    const response = await api.post(`/content-pipeline/jobs/${jobId}/cancel`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/content-pipeline/stats');
    return response.data;
  },
  trigger: async (data: {
    reason?: string;
    target_class_count?: number;
    dry_run?: boolean;
  }) => {
    const response = await api.post('/content-pipeline/trigger', data);
    return response.data;
  },
  triggerBatch: async (data: {
    count?: number;
    reason?: string;
    dry_run?: boolean;
  }) => {
    const response = await api.post('/content-pipeline/trigger/batch', data);
    return response.data;
  },
  getHistory: async (limit?: number) => {
    const response = await api.get('/content-pipeline/history', { params: { limit } });
    return response.data;
  },
  getHealth: async () => {
    const response = await api.get('/content-pipeline/health');
    return response.data;
  },
};

// Content Store API wrapper removed 2026-07-03. Scoring confirmed
// (Reply to Fable 2026-07-03) there's no plan to route facilitator-
// side content editing through FP — the wrapper had zero callers
// across the FP codebase and only added maintenance cost. Content
// editing lives in CC via `playbook-command-centre/src/lib/api.ts`
// under `contentStoreApi`. If FP ever needs read access to
// content_store, the correct path is to add a facilitator-scoped
// dependency on the read endpoints server-side, not to resurrect
// this client wrapper.

// ── Notification API ─────────────────────────────────────────────────
// Scoring's contract v1.1 (11 Oct): the signed-in facilitator's own notices,
// from the token; no id in the path.
export const notificationApi = {
  list: async (params?: { limit?: number; before?: string }): Promise<NoticeList> => {
    const response = await api.get('/me/notifications', { params });
    const d = response.data ?? {};
    return { items: Array.isArray(d.items) ? d.items : [], unread_count: Number(d.unread_count) || 0 };
  },
  pendingPopups: async (): Promise<Notice[]> => {
    const response = await api.get('/me/notifications/pending-popups');
    const d = response.data;
    return Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : [];
  },
  markShown: async (notificationId: string) => {
    await api.post(`/me/notifications/${encodeURIComponent(notificationId)}/shown`);
  },
  markRead: async (notificationId: string) => {
    await api.post(`/me/notifications/${encodeURIComponent(notificationId)}/read`);
  },
  markAllRead: async () => {
    await api.post('/me/notifications/read-all');
  },
};

// ── Difficulty Calibration API ───────────────────────────────────────
// Uses /api/difficulty prefix
const difficultyAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/difficulty`,
  headers: { 'Content-Type': 'application/json' },
});

difficultyAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('fp-access-token-v1');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

difficultyAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => (error.response?.status === 401 ? onUnauthorized(difficultyAxios, error) : Promise.reject(error)),
);

export const difficultyApi = {
  getCalibration: async (skillId: string) => {
    const response = await difficultyAxios.get(`/calibration/${skillId}`);
    return response.data;
  },
  updateCalibration: async (skillId: string, data: Record<string, unknown>) => {
    const response = await difficultyAxios.patch(`/calibration/${skillId}`, data);
    return response.data;
  },
  getBenchmarks: async () => {
    const response = await difficultyAxios.get('/benchmarks');
    return response.data;
  },
  runCalibration: async (data: { skill_ids?: string[] }) => {
    const response = await difficultyAxios.post('/calibrate', data);
    return response.data;
  },
};

// ── A/B Test API ─────────────────────────────────────────────────────
// Uses /api/ab-tests prefix (separate from /api/v1)
const abTestAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/ab-tests`,
  headers: { 'Content-Type': 'application/json' },
});

abTestAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('fp-access-token-v1');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

abTestAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => (error.response?.status === 401 ? onUnauthorized(abTestAxios, error) : Promise.reject(error)),
);

export const abTestApi = {
  listExperiments: async (status?: string) => {
    const response = await abTestAxios.get('/experiments', { params: status ? { status } : {} });
    return response.data;
  },
  getExperiment: async (experimentId: string) => {
    const response = await abTestAxios.get(`/experiments/${experimentId}`);
    return response.data;
  },
  createExperiment: async (data: {
    name: string;
    description?: string;
    arms: Record<string, { strategy: string; description: string }>;
    traffic_split: Record<string, number>;
    min_sample_per_arm?: number;
    max_duration_days?: number;
    early_stop_threshold?: number;
  }) => {
    const response = await abTestAxios.post('/experiments', data);
    return response.data;
  },
  startExperiment: async (experimentId: string) => {
    const response = await abTestAxios.post(`/experiments/${experimentId}/start`);
    return response.data;
  },
  pauseExperiment: async (experimentId: string) => {
    const response = await abTestAxios.post(`/experiments/${experimentId}/pause`);
    return response.data;
  },
  completeExperiment: async (experimentId: string) => {
    const response = await abTestAxios.post(`/experiments/${experimentId}/complete`);
    return response.data;
  },
  cancelExperiment: async (experimentId: string) => {
    const response = await abTestAxios.post(`/experiments/${experimentId}/cancel`);
    return response.data;
  },
  getResults: async (experimentId: string) => {
    const response = await abTestAxios.get(`/experiments/${experimentId}/results`);
    return response.data;
  },
  getLog: async (experimentId: string, limit?: number) => {
    const response = await abTestAxios.get(`/experiments/${experimentId}/log`, { params: { limit } });
    return response.data;
  },
};

// ── Archive API ─────────────────────────────────────────────────────
export const archiveApi = {
  list: async (params?: {
    page?: number;
    page_size?: number;
    content_type?: string;
    status?: string;
    search?: string;
  }) => {
    const response = await api.get('/archive/generations', { params });
    return response.data;
  },

  get: async (generationId: number) => {
    const response = await api.get(`/archive/generations/${generationId}`);
    return response.data;
  },

  create: async (data: {
    content_type: string;
    content_name: string;
    engine_type?: number;
    engine_name?: string;
    skills_assessed?: string[];
    difficulty?: string;
    model: string;
    prompt: string;
    output?: unknown;
    status: string;
    error_message?: string;
    tokens_in?: number;
    tokens_out?: number;
    latency_ms?: number;
    created_by?: string;
  }) => {
    const response = await api.post('/archive/generations', data);
    return response.data;
  },
};

// ── Programme API ────────────────────────────────────────────────────
export const learnersApi = {
  list: async (params?: { search?: string; status?: string; onboarding?: string; limit?: number; offset?: number }) => {
    const response = await api.get('/learners', { params });
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/learners/stats');
    return response.data;
  },
  get: async (learnerId: string) => {
    const response = await api.get(`/learners/${learnerId}`);
    return response.data;
  },
  getProgression: async (learnerId: string) => {
    const response = await api.get(`/learners/${learnerId}/progression`);
    return response.data;
  },
  getSkillHistory: async (learnerId: string, skillId: string) => {
    const response = await api.get(`/learners/${learnerId}/progression/${skillId}/history`);
    return response.data;
  },
  delete: async (learnerId: string) => {
    const response = await api.delete(`/learners/${learnerId}`);
    return response.data;
  },
};

export const programmeApi = {
  list: async (params?: { status?: string; mode?: string; learner_id?: string; limit?: number; offset?: number }) => {
    const response = await api.get('/programmes', { params });
    return response.data;
  },
  get: async (programmeId: string) => {
    const response = await api.get(`/programmes/${programmeId}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/programmes/stats');
    return response.data;
  },
  create: async (data: Record<string, unknown>) => {
    const response = await api.post('/programmes', data);
    return response.data;
  },
  updateStatus: async (programmeId: string, data: { status: string; reason?: string }) => {
    const response = await api.put(`/programmes/${programmeId}/status`, data);
    return response.data;
  },
  update: async (programmeId: string, data: Record<string, unknown>) => {
    const response = await api.patch(`/programmes/${programmeId}`, data);
    return response.data;
  },
};

// ── Workshop Scoring API ──────────────────────────────────────────────
// W6.6 (2026-04-28): facilitator-driven recording upload + scoring
// trigger. The facilitator picks a ScheduledSession in the FP, drags
// in a Zoom recording / onsite audio file, the file goes to S3 via a
// pre-signed URL, then submit() enqueues the W6.1 chord. Status polling
// drives the "Queued for scoring" panel until the session's debrief
// becomes available.

const workshopScoringAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/workshop`,
  headers: { 'Content-Type': 'application/json' },
});

workshopScoringAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('fp-access-token-v1');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // The portal's language, so server messages match it (i18n, 1 Oct).
    if (config.headers) config.headers['X-Playbook-Locale'] = currentLocale();
    return config;
  },
  (error) => Promise.reject(error),
);

workshopScoringAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Mirrors schedulerApi: don't kick the facilitator portal out on
    // 401 — that surface owns its own auth path.
    if (error.response?.status === 401) {
      if (!window.location.pathname.startsWith('/facilitator')) {
        localStorage.removeItem('fp-access-token-v1');
        window.location.href = '/login';
      } else if (requestHadToken(error.config?.headers)) {
        // FP: renew the sign-in once and retry; sign out only if that fails.
        return renewAndRetry(workshopScoringAxios, error);
      }
    }
    return Promise.reject(error);
  },
);

export interface WorkshopUploadUrlResponse {
  upload_url: string;
  s3_key: string;
  session_id: string;
  expires_in: number;
  max_size_bytes: number;
}

export interface WorkshopSubmitResponse {
  job_id: string;
  status: string;
  estimated_processing_minutes: number;
  poll_url: string;
}

export interface WorkshopScoringStatusResponse {
  status: 'queued' | 'processing' | 'awaiting_mapping' | 'complete' | 'failed';
  progress: number;
  scores_available: boolean;
  status_message?: string;
  stage?: string;
  started_at?: string;
  completed_at?: string;
  speakers_detected?: number;
}

export const workshopScoringApi = {
  /**
   * Step 1: ask for a pre-signed S3 PUT URL. Server validates file
   * size + content type, returns the URL plus the s3_key we'll pass
   * back on submit.
   */
  getUploadUrl: async (
    workshopId: string,
    body: {
      session_id?: string;
      file_name: string;
      file_size_bytes: number;
      content_type: string;
    },
  ): Promise<WorkshopUploadUrlResponse> => {
    const response = await workshopScoringAxios.post(
      `/${encodeURIComponent(workshopId)}/upload-url`,
      body,
    );
    return response.data;
  },

  /**
   * Step 2: PUT the file directly to S3 via the pre-signed URL. We
   * use a fresh axios instance (no auth interceptor — S3 will reject
   * a Bearer header). onUploadProgress drives the progress bar.
   */
  uploadToS3: async (
    uploadUrl: string,
    file: File,
    contentType: string,
    onProgress: (pct: number) => void,
  ): Promise<void> => {
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': contentType },
      onUploadProgress: (event) => {
        if (event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
      // Don't transform — S3 wants the file body as-is.
      transformRequest: [(data) => data],
    });
  },

  /**
   * Step 3: enqueue the scoring job. Server returns a job_id and a
   * poll URL for status. Triggers the W6.1 chord under the hood.
   */
  submit: async (
    workshopId: string,
    body: {
      session_id: string;
      s3_key: string;
      session_mode: 'remote' | 'onsite' | 'hybrid';
      recording_duration_sec?: number;
      bridge_session_id?: string;
      // PB-184 Flavour A (2026-05-09): pre-diarization declaration
      // of who was in the session. `speaker_label` is left null
      // because the FP facilitator submits this at upload time —
      // before diarization runs. Resolved post-diarization on the
      // backend via heuristic pairing.
      participant_mapping?: Array<{
        speaker_label?: string | null;
        learner_id?: string | null;
        display_name?: string | null;
        role?: 'participant' | 'facilitator';
      }>;
    },
  ): Promise<WorkshopSubmitResponse> => {
    const response = await workshopScoringAxios.post(
      `/${encodeURIComponent(workshopId)}/submit`,
      body,
    );
    return response.data;
  },

  /**
   * Status polling for the post-submit confirmation panel. Used by
   * the upload tab to flip from "Uploading…" → "Queued for scoring"
   * → "Scoring in progress" → "Complete".
   */
  getStatus: async (
    workshopId: string,
    sessionId: string,
  ): Promise<WorkshopScoringStatusResponse> => {
    const response = await workshopScoringAxios.get(
      `/${encodeURIComponent(workshopId)}/sessions/${encodeURIComponent(sessionId)}/status`,
    );
    return response.data;
  },
};

// ── Themes CRUD API (PB-193, 2026-05-09) ──────────────────────────
// CC theme management surface. CRUD-on-flat-file (data/focus_themes.json)
// preserves the "edit-then-commit" living-list pattern while giving CC
// a live edit affordance. Mutations invalidate the backend's lru_cache.

export interface ThemeSkillBody {
  skill_id: string;
  weight: number;
  is_core: boolean;
}

export interface ThemeBody {
  id: string;
  name: string;
  description: string;
  skills: ThemeSkillBody[];
}

const themesAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/themes`,
  headers: { 'Content-Type': 'application/json' },
});

export const themesApi = {
  list: async (): Promise<{ themes: ThemeBody[]; total_themes?: number }> => {
    const r = await themesAxios.get('');
    return r.data;
  },
  create: async (body: ThemeBody): Promise<ThemeBody> => {
    const r = await themesAxios.post('', body);
    return r.data;
  },
  update: async (themeId: string, body: ThemeBody): Promise<ThemeBody> => {
    const r = await themesAxios.put(`/${encodeURIComponent(themeId)}`, body);
    return r.data;
  },
  remove: async (themeId: string): Promise<void> => {
    await themesAxios.delete(`/${encodeURIComponent(themeId)}`);
  },
};

// ── Scenario Programmes API (CC Scenario Builder, 2026-05-16) ────────────
// CC tool that generates multi-class programmes anchored to a Scenario.
// 9 endpoints under `/api/cc/scenario-programmes`. See spec at
// `Documentation/specs/scenario-builder-v1.md`.

export type ScenarioProgrammeStatus =
  | 'draft'
  | 'outline_ready'
  | 'generating'
  | 'ready'
  | 'published'
  | 'archived';

export type ScenarioClassGenerationStatus =
  | 'pending'
  | 'generating'
  | 'ready'
  | 'failed';

export type ScenarioClassBreakdown = 'mc_only' | 'sd_only' | 'mix';
export type ScenarioDifficulty = 'easy' | 'medium' | 'hard' | 'mixed';
// Split per Nino 2026-05-17: MC vs SD have different natural ranges.
export type ScenarioMCDurationTarget = '5' | '10' | '15' | 'mixed';
export type ScenarioSDDurationTarget = '30' | '45' | '60' | '90' | 'mixed';
export type ScenarioTone = 'friendly' | 'direct' | 'formal';
// Planning-pass LLM (CC user selects on create). Kept in sync with
// backend `VALID_PLANNING_MODEL` + migration 035 CHECK constraint.
export type ScenarioPlanningModel =
  | 'claude-opus-4-6'
  | 'claude-sonnet-4-6'
  | 'claude-haiku-4-5-20251001'
  | 'gemini-2.5-pro';

export const SCENARIO_PLANNING_MODEL_LABELS: Record<ScenarioPlanningModel, string> = {
  'claude-opus-4-6': 'Claude Opus 4.6 (strongest reasoning)',
  'claude-sonnet-4-6': 'Claude Sonnet 4.6 (balanced — default)',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5 (fastest)',
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
};

export interface ScenarioOutlineRow {
  position: number;
  title: string;
  one_line: string;
  type: 'mc' | 'sd';
  anchor_skill_id: string;
  difficulty: 'easy' | 'medium' | 'hard';
  duration_target_min: number;
  /** 1..15 for mc rows (LLM-picked by Pass 1 planner); null for sd rows. */
  engine_type?: number | null;
  /** Plot id (e.g. "plot_001") for sd rows; null for mc rows. v1.1+. */
  plot_id?: string | null;
  rationale?: string;
}

export interface ScenarioClassSlot {
  position: number;
  class_id: string | null;
  outline_row: ScenarioOutlineRow | Record<string, unknown>;
  generation_status: ScenarioClassGenerationStatus;
  generation_error: string | null;
}

export interface ScenarioProgramme {
  programme_id: string;
  scenario_slug: string;
  name: string;
  audience_context: string | null;
  class_count: number;
  class_breakdown: ScenarioClassBreakdown;
  difficulty: ScenarioDifficulty;
  mc_duration_target: ScenarioMCDurationTarget;
  sd_duration_target: ScenarioSDDurationTarget;
  /** Always an array; empty when no emphasis specified. */
  anchor_skill_emphasis: string[];
  tone: ScenarioTone;
  planning_model: ScenarioPlanningModel;
  outline_json: ScenarioOutlineRow[] | null;
  status: ScenarioProgrammeStatus;
  created_by: string;
  created_at: string | null;
  published_at: string | null;
  classes: ScenarioClassSlot[];
}

export interface CreateScenarioProgrammeBody {
  scenario_slug: string;
  name: string;
  audience_context: string | null;
  class_count: number;
  class_breakdown: ScenarioClassBreakdown;
  difficulty: ScenarioDifficulty;
  mc_duration_target: ScenarioMCDurationTarget;
  sd_duration_target: ScenarioSDDurationTarget;
  anchor_skill_emphasis: string[] | null;
  tone: ScenarioTone;
  planning_model?: ScenarioPlanningModel;
}

const scenarioProgrammesAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/cc/scenario-programmes`,
  headers: { 'Content-Type': 'application/json' },
});

export const scenarioProgrammesApi = {
  list: async (filters: {
    scenario_slug?: string;
    status?: ScenarioProgrammeStatus;
    limit?: number;
  } = {}): Promise<{ programmes: ScenarioProgramme[] }> => {
    const r = await scenarioProgrammesAxios.get('', { params: filters });
    return r.data;
  },
  create: async (body: CreateScenarioProgrammeBody): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post('', body);
    return r.data;
  },
  get: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.get(`/${encodeURIComponent(programme_id)}`);
    return r.data;
  },
  plan: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/plan`);
    return r.data;
  },
  updateOutline: async (
    programme_id: string,
    outline: ScenarioOutlineRow[],
  ): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.patch(
      `/${encodeURIComponent(programme_id)}/outline`,
      { outline },
    );
    return r.data;
  },
  generate: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/generate`);
    return r.data;
  },
  regenerateClass: async (
    programme_id: string,
    position: number,
  ): Promise<ScenarioClassSlot> => {
    const r = await scenarioProgrammesAxios.post(
      `/${encodeURIComponent(programme_id)}/classes/${position}/regenerate`,
    );
    return r.data;
  },
  publish: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/publish`);
    return r.data;
  },
  archive: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/archive`);
    return r.data;
  },
  reset: async (programme_id: string): Promise<ScenarioProgramme> => {
    const r = await scenarioProgrammesAxios.post(`/${encodeURIComponent(programme_id)}/reset`);
    return r.data;
  },
  remove: async (programme_id: string): Promise<void> => {
    await scenarioProgrammesAxios.delete(`/${encodeURIComponent(programme_id)}`);
  },
};

// Scenarios catalogue (for the dropdown on the create form). Reads from
// the public `/api/scenarios` endpoint — same surface Web uses.
const scenariosAxios: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/scenarios`,
  headers: { 'Content-Type': 'application/json' },
});

export interface ScenarioListItem {
  id: string;
  slug: string;
  name: string;
  pitch: string;
  anchor_skills: string[];
  pillar_mix: string[];
}

export const scenariosApi = {
  list: async (): Promise<{ scenarios: ScenarioListItem[] }> => {
    const r = await scenariosAxios.get('');
    return r.data;
  },
};

// Card Library API (Nudge and Knowledge Cards)
export const cardApi = {
  // Nudge cards
  listNudge: async (params?: Record<string, unknown>) => {
    const r = await api.get('/cards/nudge', { params });
    return r.data;
  },
  getNudge: async (id: string) => {
    const r = await api.get(`/cards/nudge/${id}`);
    return r.data;
  },
  createNudge: async (data: Record<string, unknown>) => {
    const r = await api.post('/cards/nudge', data);
    return r.data;
  },
  updateNudge: async (id: string, data: Record<string, unknown>) => {
    const r = await api.patch(`/cards/nudge/${id}`, data);
    return r.data;
  },
  deleteNudge: async (id: string) => {
    const r = await api.delete(`/cards/nudge/${id}`);
    return r.data;
  },
  nudgeByEngine: async (engineType: number) => {
    const r = await api.get(`/cards/nudge/by-engine/${engineType}`);
    return r.data;
  },

  // Knowledge cards
  listKnowledge: async (params?: Record<string, unknown>) => {
    const r = await api.get('/cards/knowledge', { params });
    return r.data;
  },
  getKnowledge: async (id: string) => {
    const r = await api.get(`/cards/knowledge/${id}`);
    return r.data;
  },
  createKnowledge: async (data: Record<string, unknown>) => {
    const r = await api.post('/cards/knowledge', data);
    return r.data;
  },
  updateKnowledge: async (id: string, data: Record<string, unknown>) => {
    const r = await api.patch(`/cards/knowledge/${id}`, data);
    return r.data;
  },
  deleteKnowledge: async (id: string) => {
    const r = await api.delete(`/cards/knowledge/${id}`);
    return r.data;
  },
};

// ── Facilitator Marketplace API (Nino 2026-05-20) ──────────────────────
// Backs the FP "Active Skill Drills" tab. Hits /api/facilitator-marketplace
// on the same backend, with the same auth header pattern as schedulerApi.

const rootApi: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

rootApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('fp-access-token-v1');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // The portal's language, so server messages match it (i18n, 1 Oct).
  if (config.headers) config.headers['X-Playbook-Locale'] = currentLocale();
  return config;
});

rootApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      if (!window.location.pathname.startsWith('/facilitator')) {
        localStorage.removeItem('fp-access-token-v1');
        window.location.href = '/login';
      } else if (requestHadToken(error.config?.headers)) {
        // FP: renew the sign-in once and retry; sign out only if that fails.
        return renewAndRetry(rootApi, error);
      }
    }
    return Promise.reject(error);
  },
);

export interface ActiveDrillRow {
  workshop_id: string;
  title: string;
  pillars: string[];
  skills_assessed: string[];
  duration_min: number;
  difficulty: string;
  max_participants: number;
  min_participants: number;
  upcoming_sessions: number;
  bookings_in_upcoming: number;
  my_claimed_upcoming: number;
}

export interface HeatmapResponse {
  workshop_id: string;
  grid: number[][];
  signal_sources: string[];
}

export interface ClaimedSessionRow {
  session_id: string;
  workshop_id: string;
  workshop_title: string;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  max_participants: number;
  confirmed_count: number;
  status: string;
}

export const facilitatorMarketplaceApi = {
  listActiveDrills: async (): Promise<ActiveDrillRow[]> => {
    const r = await rootApi.get('/api/facilitator-marketplace/active-drills');
    // The hours learners can book, when the server sends them (Nino, 3 Oct).
    setServerWindow(r.data.bookable_window);
    return r.data.drills || [];
  },
  getHeatmap: async (workshopId: string): Promise<HeatmapResponse> => {
    const r = await rootApi.get(
      `/api/facilitator-marketplace/active-drills/${workshopId}/demand-heatmap`,
    );
    return r.data;
  },
  claimSlot: async (
    workshopId: string,
    body: {
      start_time_utc: string;
      capacity_max?: number;
      // Live Coach venue model (Layer 2, 2026-06-06). Optional; when
      // present, backend runs a region-haversine distance check vs
      // facilitator's home_region + travel_radius_km. Omit for online.
      venue_region?: string | null;
    },
  ): Promise<ClaimedSessionRow> => {
    const r = await rootApi.post(
      `/api/facilitator-marketplace/active-drills/${workshopId}/claim`,
      body,
    );
    return r.data;
  },
  myClaimed: async (): Promise<ClaimedSessionRow[]> => {
    const r = await rootApi.get('/api/facilitator-marketplace/my-claimed');
    return r.data.sessions || [];
  },
  // cancelClaim removed 2 Oct (Nino): giving up a session is Withdraw only,
  // via schedulerApiClient.facilitatorWithdraw, which tries another facilitator first.
  // Wave 1.2 (2026-06-12) — authed facilitator's own trust score.
  // Reads the same formula CC's Facilitators Leaderboard reads.
  myTrust: async (): Promise<MyTrustResponse> => {
    const r = await rootApi.get('/api/facilitator-marketplace/me/trust');
    return r.data;
  },
  // Wave 2.2 (2026-06-12) — per-channel + per-event notification opt-in.
  getNotificationPreferences: async (): Promise<MyNotificationPreferencesResponse> => {
    const r = await rootApi.get('/api/facilitator-marketplace/me/notification-preferences');
    return r.data;
  },
  updateNotificationPreferences: async (
    body: NotificationPreferences,
  ): Promise<MyNotificationPreferencesResponse> => {
    const r = await rootApi.put(
      '/api/facilitator-marketplace/me/notification-preferences',
      body,
    );
    return r.data;
  },
  // Wave 2.6 (2026-06-12) — authed facilitator's earnings ledger.
  myEarnings: async (): Promise<MyEarningsResponse> => {
    const r = await rootApi.get('/api/facilitator-marketplace/me/earnings');
    return r.data;
  },
  // Wave 2.8 (2026-06-12) — subscribable iCalendar feed URL.
  myCalendarFeedUrl: async (): Promise<MyCalendarFeedResponse> => {
    const r = await rootApi.get('/api/facilitator-marketplace/me/calendar-feed-url');
    return r.data;
  },
  // Wave 2.8 follow-up — per-facilitator token rotation (migration 065).
  // Bumps the version on the facilitator row; old URL becomes a 404.
  rotateCalendarFeedUrl: async (): Promise<MyCalendarFeedResponse> => {
    const r = await rootApi.post('/api/facilitator-marketplace/me/calendar-feed-url/rotate');
    return r.data;
  },
  // Earnings part 2 (4 Oct): the shapes Tools proposed to Scoring; Scoring's
  // names win when they plan it (lib/payouts.ts). Off until payouts_live.
  /** Bank + last 4 only, never the full number; null when none is set. */
  myPayoutAccount: async (): Promise<PayoutAccount | null> => {
    const r = await rootApi.get('/api/facilitator-marketplace/me/payout-account');
    return r.data && r.data.last4 ? r.data : null;
  },
  /** The account number travels in this body only: never a URL, never logged. */
  savePayoutAccount: async (body: { bank_code: string; account_number: string; account_holder: string }): Promise<PayoutAccount> => {
    const r = await rootApi.put('/api/facilitator-marketplace/me/payout-account', body);
    return r.data;
  },
  payoutBanks: async (country: string): Promise<PayoutBank[]> => {
    const r = await rootApi.get('/api/facilitator-marketplace/payout-banks', { params: { country } });
    return Array.isArray(r.data) ? r.data : (r.data?.banks ?? []);
  },
  myNextPayout: async (): Promise<NextPayout | null> => {
    const r = await rootApi.get('/api/facilitator-marketplace/me/payouts/next');
    return r.data && r.data.date ? r.data : null;
  },
  myPayouts: async (limit = 12): Promise<PayoutRun[]> => {
    const r = await rootApi.get('/api/facilitator-marketplace/me/payouts', { params: { limit } });
    return Array.isArray(r.data) ? r.data : (r.data?.payouts ?? []);
  },
  myPayout: async (payoutId: string): Promise<PayoutRunDetail> => {
    const r = await rootApi.get(`/api/facilitator-marketplace/me/payouts/${encodeURIComponent(payoutId)}`);
    return r.data;
  },
};

// Wave 2.8 — subscribable .ics feed URL.
export interface MyCalendarFeedResponse {
  facilitator_id: string;
  feed_url: string;  // path; FP composes the absolute URL with window.location.origin
}

// Wave 2.6 (2026-06-12) — earnings shapes. Mirrors MyEarningsResponse
// Pydantic model in src/api/facilitator_marketplace_endpoints.py.
export interface EarningsLedgerRow {
  ledger_id: string;
  session_id: string | null;
  country_code: string;
  base_rate_usd: number;
  tier: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  tier_multiplier: number;
  duration_minutes: number;
  gross_usd: number;
  scheduled_start: string | null;
  created_at: string;
  /** Nino 1 Oct: written 'pending' when the drill ends, settled on
   *  completion (Scoring to add). Absent on older rows = settled. */
  status?: string;
  /** New rows (Scoring, after the 2 Oct re-test): amount in `currency`. */
  gross?: number;
  currency?: string;
  /** The drill's name (asked of Scoring, 4 Oct). */
  workshop_title?: string;
  /** BM's names for the payout-currency amount (4 Oct). */
  gross_local?: number;
  payout_currency?: string;
}
export interface EarningsMonthlySummary {
  year_month: string;
  session_count: number;
  gross_usd: number;
  /** In the payout currency (asked of Scoring, 4 Oct). */
  gross?: number;
  gross_local?: number;
}
/** Shipped 2026-07-19 (Scoring, FP-2 backend + FP-3 lifetime count):
 *  distinguishes a genuine no-earnings-yet from a region-miss silent
 *  $0 payout. `active` = payout going through; `pending_profile_fix`
 *  = facilitator's home_region isn't in the payout mapping (EU/US/AU
 *  facilitators pre-broadening) so no earnings row is written. */
// The server sends 'ok' (checked 4 Oct); 'active' kept for older builds.
export type PayoutStatus = 'ok' | 'active' | 'pending_profile_fix';

export interface MyEarningsResponse {
  facilitator_id: string;
  currency: string;
  lifetime_gross_usd: number;
  /** 2026-07-19 (FP-3 consumer wire): real lifetime session count.
   *  Was previously derived client-side from ledger.length (capped at
   *  50) then from sum(monthly_summary[].session_count) (12-month
   *  window). Now honest. */
  lifetime_session_count: number;
  current_month_gross_usd: number;
  current_month_session_count: number;
  /** Totals in the payout currency (asked of Scoring, 4 Oct). */
  current_month_gross?: number;
  lifetime_gross?: number;
  current_month_gross_local?: number;
  lifetime_gross_local?: number;
  payout_currency?: string;
  /** True once BM's Monday payout run is on (asked of Scoring, 4 Oct). */
  payouts_live?: boolean;
  /** 2026-07-19 (FP-2 consumer wire): payout state. 'active' by
   *  default; 'pending_profile_fix' when the facilitator's region
   *  isn't yet in the payout mapping so completed sessions produce
   *  no earnings row. */
  payout_status: PayoutStatus;
  /** Free-text reason when payout_status !== 'active'. Rendered
   *  verbatim as the surface copy. */
  payout_blocked_reason: string | null;
  /** Scoring, 4 Oct (ships early): region_missing, dispute_open, bank_unreachable, bank_details_missing, account_*. */
  payout_blocked_code?: string | null;
  monthly_summary: EarningsMonthlySummary[];
  ledger: EarningsLedgerRow[];
}

// Wave 2.2 (2026-06-12) — notification preferences shapes. Mirrors the
// Pydantic models in src/api/facilitator_marketplace_endpoints.py.
export interface NotificationPreferences {
  email: Record<string, boolean>;
  whatsapp: Record<string, boolean>;
}

export interface MyNotificationPreferencesResponse {
  facilitator_id: string;
  preferences: NotificationPreferences;
  // Catalogue echoed back from the server so the FP renders the
  // toggle list without hard-coding event names.
  email_events: string[];
  whatsapp_events: string[];
}

// Wave 1.2 (2026-06-12) — response shape for /me/trust. Mirrors the
// MyTrustResponse Pydantic model in
// src/api/facilitator_marketplace_endpoints.py.
export interface MyTrustResponse {
  facilitator_id: string;
  trust_score: number;            // 0-100
  trust_band: 'excellent' | 'good' | 'developing' | 'watch' | 'at-risk';
  sessions_delivered: number;
  cancelled_by_facilitator: number;
  no_shows: number;
  completion_rate: number;        // 0.0-1.0
  no_show_rate: number;           // 0.0-1.0
  avg_facilitator_rating: number | null;
  n_ratings: number;
  ratings_pending: boolean;
}

// ── Facilitator profile (Live Coach venue model, Layer 4 — 2026-06-06)
// Dedicated endpoint for the two venue-driven fields (home_region +
// travel_radius_km). Lives separately from the legacy
// /facilitators/{id} PATCH so the FP can update venue settings without
// touching the scheduler payload shape.

export interface FacilitatorProfileResponse {
  facilitator_id: string;
  home_region: string | null;
  travel_radius_km: number;
}

export const facilitatorProfileApi = {
  /**
   * A facilitator edits their own account (Scoring 2026-09-29). The server
   * takes the facilitator from the token, never the URL, and accepts only
   * these fields. Replaces the admin-only PATCH /scheduler/facilitators/{id},
   * which refused facilitators.
   */
  updateMyAccount: async (body: {
    display_name?: string;
    timezone?: string;
    profile_data?: Record<string, unknown>;
    max_sessions_per_day?: number;
    max_sessions_per_week?: number;
  }) => {
    const r = await rootApi.patch('/api/facilitator/me', body);
    return r.data;
  },
  updateMe: async (body: {
    home_region?: string | null;
    travel_radius_km?: number;
  }): Promise<FacilitatorProfileResponse> => {
    const r = await rootApi.patch('/api/facilitator/me/profile', body);
    return r.data;
  },
};

// ── Live Coach slot venue (Layer 2b — 2026-06-06) ───────────────────
// Read-only client for the per-session venue endpoint. SessionsTab uses
// it to render venue badges + the assigned-drill detail uses it for
// address + notes.

export interface LiveCoachVenueRefDTO {
  id: string;
  name: string;
  address?: string | null;
  region?: string | null;
  capacity?: number | null;
  notes?: string | null;
}

export interface LiveCoachSlotDTO {
  session_id: string;
  workshop_id: string;
  workshop_title: string;
  short_title?: string | null;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  facilitator_id?: string | null;
  status: string;
  venue: LiveCoachVenueRefDTO | null;
  is_online: boolean;
}

export const liveCoachSlotApi = {
  get: async (sessionId: string): Promise<LiveCoachSlotDTO> => {
    const r = await rootApi.get(`/api/live-coach/slots/${sessionId}`);
    return r.data;
  },
};

// ── CC Facilitators (admin trust indicator) ──────────────────────────

export interface FacilitatorTrustRow {
  facilitator_id: string;
  display_name: string;
  email: string;
  status: string;
  sessions_delivered: number;
  upcoming_sessions: number;
  cancelled_by_facilitator: number;
  no_shows: number;
  avg_facilitator_rating: number | null;
  n_ratings: number;
  completion_rate: number;
  no_show_rate: number;
  trust_score: number;
  trust_band: 'excellent' | 'good' | 'developing' | 'watch' | 'at-risk';
}

export interface FacilitatorDetail {
  facilitator: FacilitatorTrustRow;
  recent_sessions: Array<{
    session_id: string;
    workshop_title: string;
    scheduled_start: string;
    status: string;
    confirmed_count: number;
    avg_rating: number | null;
  }>;
}

export const ccFacilitatorsApi = {
  list: async (): Promise<FacilitatorTrustRow[]> => {
    const r = await rootApi.get('/api/admin/cc/facilitators');
    return r.data.facilitators || [];
  },
  detail: async (facilitatorId: string): Promise<FacilitatorDetail> => {
    const r = await rootApi.get(`/api/admin/cc/facilitators/${facilitatorId}`);
    return r.data;
  },
};

export default api;
